import { OrderMarginType } from '@flipster/utils/types/base';
import { valueof } from '@flipster/utils/typescript';

export interface ResPagination {
  hasPrev: boolean;
  hasNext: boolean;
}

export interface ReqPagination {
  before?: string;
  after?: string;
  count: number;
}

export interface ReqDatetime {
  startTime?: string | undefined;
  endTime?: string | undefined;
}

export type UUID = string;
export type Timestamp = string; // Epoch time in nanoseconds as string

export interface TradeRequestBase {
  requestId: UUID; // uuid
  timestamp: Timestamp;
  refServerTimestamp?: Timestamp;
  refClientTimestamp?: Timestamp;
  attribution?: string;
  /** Sub-account ID for copy trading. If omitted, treated as the main account's request. */
  accountId?: number;
}

export interface OneWayOrderCreationRequestBase extends TradeRequestBase {
  reduceOnly: boolean;
  marginType?: OrderMarginType;
}

export interface OpenedPosition {
  symbol: string;
  slot: number;
  avgPrice: string;
  liqPrice?: string;
  size: string;
  leverage: number;
  initMargin: string;
  marginAssigned: string;
  achievedFlipComboCount?: number;
  positionId: string;
  autoSl?: {
    status: string;
    triggerPrice?: string;
  };
  remainingQty?: string;
}

export interface SpotMarketOrderAsset {
  symbol: string;
  avgPrice: string;
  qty: string;
  amount: string;
}

export const OrderSideEnum = {
  Buy: 'ORDER_SIDE_BUY',
  Sell: 'ORDER_SIDE_SELL',
  Invalid: 'ORDER_SIDE_INVALID',
} as const;

export type OrderSideType = valueof<typeof OrderSideEnum>;

export const ConditionalOrderTypeEnum = {
  Invalid: 'CONDITIONAL_ORDER_TYPE_INVALID',
  PositionTP: 'CONDITIONAL_ORDER_TYPE_POSITION_TP',
  PositionSL: 'CONDITIONAL_ORDER_TYPE_POSITION_SL',
  OrderTP: 'CONDITIONAL_ORDER_TYPE_ORDER_TP',
  OrderSL: 'CONDITIONAL_ORDER_TYPE_ORDER_SL',
} as const;

export type ConditionalOrderTypeType = valueof<typeof ConditionalOrderTypeEnum>;

export const ExecInstEnum = {
  Invalid: 'EXEC_INST_INVALID',
  DoNotInitiate: 'EXEC_INST_PARTICIPATE_DO_NOT_INITIATE',
  ReduceOnly: 'EXEC_INST_REDUCE_ONLY',
  Close: 'EXEC_INST_CLOSE',
} as const;

export type ExecInstType = valueof<typeof ExecInstEnum>;

export const OrderCancelReasonEnum = {
  Invalid: 'ORDER_CANCEL_REASON_INVALID',
  Immediate: 'ORDER_CANCEL_REASON_IMMEDIATE_OR_CANCEL',
  PostOnly: 'ORDER_CANCEL_REASON_POST_ONLY_CANCEL',
  ReduceOnly: 'ORDER_CANCEL_REASON_REDUCE_ONLY_CANCEL',
  UserRequested: 'ORDER_CANCEL_REASON_USER_REQUESTED',
  Supervisory: 'ORDER_CANCEL_REASON_SUPERVISORY',
} as const;

export type OrderCancelReasonType = valueof<typeof OrderCancelReasonEnum>;

export const OrderStatusEnum = {
  New: 'ORDER_STATUS_NEW',
  PendingNew: 'ORDER_STATUS_PENDING_NEW',
  PartiallyFilled: 'ORDER_STATUS_PARTIALLY_FILLED',
  Filled: 'ORDER_STATUS_FILLED',
  Canceled: 'ORDER_STATUS_CANCELED',
  Rejected: 'ORDER_STATUS_REJECTED',
  Invalid: 'ORDER_STATUS_INVALID',
} as const;

export type OrderStatusType = valueof<typeof OrderStatusEnum>;

export const OrderTypeEnum = {
  Market: 'ORDER_TYPE_MARKET',
  Limit: 'ORDER_TYPE_LIMIT',
  StopLimit: 'ORDER_TYPE_STOP_LIMIT',
  StopMarket: 'ORDER_TYPE_STOP_MARKET',
  Liquidation: 'ORDER_TYPE_LIQUIDATION',
  Settlement: 'ORDER_TYPE_SETTLEMENT',
  Invalid: 'ORDER_TYPE_INVALID',
  Takeover: 'ORDER_TYPE_TAKEOVER',
  TakeoverSettlement: 'ORDER_TYPE_TAKEOVER_SETTLEMENT',
} as const;

export type OrderTypeType = valueof<typeof OrderTypeEnum>;

export interface OrderBase {
  avgPrice: string;
  clientOrderId: string;
  conditionalOrderType: ConditionalOrderTypeType;
  cumQty: string;
  execInst: ExecInstType;
  leavesQty: string;
  orderCancelReason: OrderCancelReasonType;
  orderId: string;
  orderQty: string;
  orderStatus: OrderStatusType;
  orderTime: number;
  orderType: OrderTypeType;
  price: string;
  side: OrderSideType;
  specialFlag: string;
  symbol: string;
  text: string;
  timeInForce: string;
  transactTime: number;
}
